import { useEffect, useState } from 'react';

import {
  fetchPlanDetails,
  fetchSettingsApi,
  selectPlanDetails,
  selectSettings,
} from 'src/features/settings-slice';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Box } from 'wix-style-react';
import styles from './PlanInformation.module.scss';
import { useSelector } from 'react-redux';
import { capitalize } from 'lodash';
import { Button } from 'src/redesign/components/Button';
import { useHistory } from 'react-router-dom';
import {
  fetchTicketPacks,
  fetchSubscriptionApi,
  selectSubscription,
} from 'src/features/site-slice';
import {
  hasRequestEventCreationRoleSelector,
  isAdminOrOwnerSelector,
} from 'src/redesign/features/Account/store/selectors';
import { getSelectedSiteDataSelector } from '../SelectedSite/store';
import { UsageCreditsDialog } from './components/UsageCreditsDialog';

export const AttendeeQuotaAmount = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const planDetails = useAppSelector(selectPlanDetails);
  const settings = useAppSelector(selectSettings);
  const site = useSelector(getSelectedSiteDataSelector);
  const subscription = useAppSelector(selectSubscription);
  const history = useHistory();
  const hasEventCreatorRole =
    site?.roles?.includes('EVENT_CREATOR') && !site?.roles?.includes('ADMIN');
  const hasRequestEventCreationRole = useAppSelector(hasRequestEventCreationRoleSelector);
  const isAdminOrOwner = useAppSelector(isAdminOrOwnerSelector);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await Promise.all([
        dispatch(fetchPlanDetails()),
        dispatch(fetchSettingsApi()),
        dispatch(fetchSubscriptionApi(site.id)),
        dispatch(fetchTicketPacks()),
      ]);

      setLoading(false);
    }

    if (!hasEventCreatorRole) {
      void fetchData();
    }
  }, [dispatch, hasEventCreatorRole]);

  // Don't render component for REQUEST_EVENT_CREATION role users
  if (hasEventCreatorRole && hasRequestEventCreationRole && !isAdminOrOwner) {
    return null;
  }

  // Use centralized calculations from backend (with fallbacks for backwards compatibility)
  const totalTicketCapacity =
    planDetails?.total_ticket_capacity ||
    (planDetails?.monthly_quota || 0) + (planDetails?.pack_ticket_balance || 0);
  const totalEmailCapacity =
    planDetails?.total_email_capacity ||
    (planDetails?.monthly_email_sent_quota || 0) + (planDetails?.pack_email_balance || 0);

  const quotaPercentage =
    planDetails?.ticket_usage_percentage !== undefined
      ? planDetails.ticket_usage_percentage
      : totalTicketCapacity > 0
        ? ((planDetails?.monthly_attendee_transaction || 0) / totalTicketCapacity) * 100
        : 0;

  const emaiQuotaPercentage =
    planDetails?.email_usage_percentage !== undefined
      ? planDetails.email_usage_percentage
      : totalEmailCapacity > 0
        ? ((planDetails?.monthly_emails_sent || 0) / totalEmailCapacity) * 100
        : 0;

  const upgradeLink = `/events/billing`;

  const formatPlanName = (planName: string | undefined): string => {
    if (!planName) return '';

    // Map specific plan names
    if (planName.toLowerCase() === 'featured') {
      return 'Professional';
    }

    // Check if the plan name contains a hyphen and a number
    const match = /^(enterprise)-(\d+)$/i.exec(planName);
    if (match) {
      const [_, name, number] = match;
      const formattedName = capitalize(name);
      const formattedNumber = Number(number).toLocaleString();
      return `${formattedName} ${formattedNumber}`;
    }

    return capitalize(planName);
  };

  // Check if user has ticket packs
  const hasTicketPacks = (planDetails?.pack_ticket_balance || 0) > 0;

  // Check if user is on free plan
  const isFreePlan = planDetails?.plan_name?.toLowerCase() === 'free';

  // Build the label
  const basePlanLabel = planDetails?.in_free_trial
    ? `Free Trial (${settings?.trialDaysLeft} Days Left)`
    : `${formatPlanName(planDetails?.plan_name)} Plan`;

  const label = hasTicketPacks ? `${basePlanLabel} + Ticket Packs` : basePlanLabel;

  // Use centralized quota exceeded check from backend (with fallback)
  const isQuotaExceeded =
    planDetails?.is_quota_exceeded !== undefined
      ? planDetails.is_quota_exceeded || (isFreePlan && !hasTicketPacks)
      : quotaPercentage >= 100 || emaiQuotaPercentage >= 100 || (isFreePlan && !hasTicketPacks);

  // Check if on a paid plan or free plan with ticket packs (not exceeded)
  const isPaidPlan = (!isFreePlan || hasTicketPacks) && !isQuotaExceeded;
  return (
    <>
      <Box direction="vertical">
        <Box align="right">
          <Button
            className={`${styles.businessPlanButton} ${isQuotaExceeded ? styles.quotaExceeded : ''} ${isPaidPlan ? styles.paidPlan : ''}`}
            priority={isQuotaExceeded ? 'primary' : 'secondary'}
            size="large"
            prefixIcon={<i className="pi pi-chart-bar" />}
            onClick={() => setDialogVisible(true)}
            disabled={loading}
          >
            {loading ? 'Loading Plan...' : `${label} • View Usage`}
          </Button>
        </Box>
      </Box>

      <UsageCreditsDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        planDetails={planDetails}
        subscription={subscription}
      />
    </>
  );
};
